import React, { forwardRef, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { FixedSizeList as List } from 'react-window';
import dayjs from 'dayjs';
import ReservationBar from './ReservationBar';
import {
  MONTH_ROW_HEIGHT,
  WEEK_ROW_HEIGHT,
  DAY_ROW_HEIGHT,
  RATE_ROW_HEIGHT
} from '../constants/layout';

// Festivos (puede venir como prop en el futuro). De momento usa lista vacía.
const HOLIDAYS = new Set();

const TimelinePane = forwardRef(function TimelinePane(
  {
    refList,
    xScrollRef,
    rooms,
    reservationsByRoom,
    headers,
    viewMode,
    cellWidth,
    timelineWidth,
    height,
    rowHeight,
    rangeStart,
    totalDays,
    onOpenReservation,
    onSyncScroll,
    pmsColors,
    rateAvailability // opcional: { 'YYYY-MM-DD': { avail: number, totalRooms: number, rate: number } }
  },
  _
) {
  const handleScroll = ({ scrollOffset, scrollUpdateWasRequested }) => {
    if (!scrollUpdateWasRequested) onSyncScroll(scrollOffset);
  };

  // Calcula disponibilidad por día desde reservations si no viene como prop
  const computedAvail = useMemo(() => {
    if (rateAvailability) return rateAvailability;
    if (viewMode === 'hourly') return {};
    const byDay = {};
    if (!headers.days) return byDay;
    const totalRooms = rooms.length || 1;
    headers.days.forEach((d) => {
      const day = dayjs(d.date).startOf('day');
      let occ = 0;
      reservationsByRoom.forEach((arr) => {
        const used = arr.some((r) => {
          const ci = dayjs(r.start).startOf('day');
          const co = dayjs(r.end).startOf('day');
          return day.isSame(ci) || (day.isAfter(ci) && day.isBefore(co));
        });
        if (used) occ++;
      });
      const avail = Math.max(0, totalRooms - occ);
      byDay[day.format('YYYY-MM-DD')] = { avail, totalRooms };
    });
    return byDay;
  }, [rateAvailability, viewMode, headers, reservationsByRoom, rooms]);

  // Posición en píxeles del momento "ahora" dentro del rango (para la línea vertical)
  const todayLineLeft = useMemo(() => {
    if (viewMode === 'hourly') {
      const now = dayjs();
      const isToday = dayjs(rangeStart).isSame(now, 'day');
      if (!isToday) return null;
      return (now.hour() + now.minute() / 60) * cellWidth;
    }
    const today = dayjs().startOf('day');
    const start = dayjs(rangeStart).startOf('day');
    const diff = today.diff(start, 'day');
    if (diff < 0 || diff >= totalDays) return null;
    return diff * cellWidth + cellWidth / 2;
  }, [viewMode, rangeStart, cellWidth, totalDays]);

  // Header horario — usa la misma altura total (124px) que el rango para alinear con el sidebar
  const renderHourlyHeader = () => {
    const dateLabel = dayjs(rangeStart).format('dddd, DD [de] MMMM, YYYY');
    return (
      <Box>
        {/* Fila 1 (28px) — equivalente a "Meses": fecha completa */}
        <Box
          sx={{
            display: 'flex',
            height: MONTH_ROW_HEIGHT,
            backgroundColor: '#F3F5FA',
            borderBottom: `1px solid ${pmsColors.ui.border}`,
            alignItems: 'center',
            pl: 1.5
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 800,
              textTransform: 'uppercase',
              fontSize: '0.7rem',
              letterSpacing: '0.8px',
              color: pmsColors.text.secondary
            }}
          >
            {dateLabel}
          </Typography>
        </Box>

        {/* Fila 2 (22px) — equivalente a "Semanas": AM / PM */}
        <Box
          sx={{
            display: 'flex',
            height: WEEK_ROW_HEIGHT,
            backgroundColor: pmsColors.ui.sidebar,
            borderBottom: `1px solid ${pmsColors.ui.border}`,
            position: 'relative'
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              top: 0,
              width: 12 * cellWidth,
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRight: `1px solid ${pmsColors.ui.border}`
            }}
          >
            <Typography variant="caption" sx={{ fontSize: '0.66rem', color: pmsColors.text.secondary, fontWeight: 600 }}>
              AM
            </Typography>
          </Box>
          <Box
            sx={{
              position: 'absolute',
              left: 12 * cellWidth,
              top: 0,
              width: 12 * cellWidth,
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Typography variant="caption" sx={{ fontSize: '0.66rem', color: pmsColors.text.secondary, fontWeight: 600 }}>
              PM
            </Typography>
          </Box>
        </Box>

        {/* Fila 3 (46px) — equivalente a "Días": las horas */}
        <Box
          sx={{
            display: 'flex',
            height: DAY_ROW_HEIGHT,
            backgroundColor: pmsColors.ui.surface,
            borderBottom: `1px solid ${pmsColors.ui.divider}`
          }}
        >
          {headers.hours.map((h, i) => {
            const now = dayjs();
            const isToday = dayjs(rangeStart).isSame(now, 'day');
            const isCurrentHour = isToday && now.hour() === h.hour;
            return (
              <Box
                key={i}
                sx={{
                  width: cellWidth,
                  borderLeft: `1px solid ${h.hour % 6 === 0 ? pmsColors.ui.divider : pmsColors.ui.border}`,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '2px',
                  backgroundColor: h.hour < 8 || h.hour > 20 ? pmsColors.ui.background : 'transparent'
                }}
              >
                <Box
                  sx={{
                    width: 26,
                    height: 26,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: isCurrentHour ? pmsColors.secondary.main : 'transparent',
                    color: isCurrentHour ? '#fff' : pmsColors.text.primary,
                    fontWeight: 700,
                    fontSize: '0.78rem',
                    lineHeight: 1,
                    boxShadow: isCurrentHour ? '0 2px 6px rgba(99,102,241,.35)' : 'none'
                  }}
                >
                  {h.label}
                </Box>
              </Box>
            );
          })}
        </Box>

        {/* Fila 4 (28px) — equivalente a la "franja de tarifa": etiquetas Madrugada/Mañana/Tarde/Noche */}
        <Box
          sx={{
            display: 'flex',
            height: RATE_ROW_HEIGHT,
            backgroundColor: '#FAFBFF',
            borderBottom: `1px solid ${pmsColors.ui.border}`,
            position: 'relative'
          }}
        >
          {[
            { from: 0, to: 6, label: 'Madrugada', color: '#64748B' },
            { from: 6, to: 12, label: 'Mañana', color: '#16A34A' },
            { from: 12, to: 18, label: 'Tarde', color: '#D97706' },
            { from: 18, to: 24, label: 'Noche', color: '#7C3AED' }
          ].map((seg, i) => (
            <Box
              key={i}
              sx={{
                position: 'absolute',
                left: seg.from * cellWidth,
                top: 0,
                width: (seg.to - seg.from) * cellWidth,
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderLeft: `1px solid ${pmsColors.ui.border}`
              }}
            >
              <Typography sx={{ fontSize: '0.66rem', fontWeight: 800, color: seg.color }}>
                {seg.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  // Header de rango (mes / semana / día / tarifa)
  const renderRangeHeader = () => (
    <Box>
      {/* Meses */}
      <Box
        sx={{
          display: 'flex',
          height: MONTH_ROW_HEIGHT,
          backgroundColor: '#F3F5FA',
          borderBottom: `1px solid ${pmsColors.ui.border}`
        }}
      >
        <Box sx={{ position: 'relative', flex: 1, overflow: 'hidden' }}>
          <Box sx={{ position: 'relative', width: timelineWidth, height: '100%' }}>
            {headers.months.map((month, i) => (
              <Box
                key={i}
                sx={{
                  position: 'absolute',
                  left: month.start * cellWidth,
                  top: 0,
                  width: month.width,
                  height: '100%',
                  borderRight: `1px solid ${pmsColors.ui.border}`,
                  display: 'flex',
                  alignItems: 'center',
                  pl: 1.5
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 800,
                    textTransform: 'uppercase',
                    fontSize: '0.7rem',
                    letterSpacing: '0.8px',
                    color: pmsColors.text.secondary
                  }}
                >
                  {month.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Semanas */}
      <Box
        sx={{
          display: 'flex',
          height: WEEK_ROW_HEIGHT,
          backgroundColor: pmsColors.ui.sidebar,
          borderBottom: `1px solid ${pmsColors.ui.border}`
        }}
      >
        <Box sx={{ position: 'relative', flex: 1, overflow: 'hidden' }}>
          <Box sx={{ position: 'relative', width: timelineWidth, height: '100%' }}>
            {headers.weeks.map((week, i) => (
              <Box
                key={i}
                sx={{
                  position: 'absolute',
                  left: week.start * cellWidth,
                  top: 0,
                  width: week.width,
                  height: '100%',
                  borderRight: `1px solid ${pmsColors.ui.border}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ fontSize: '0.66rem', color: pmsColors.text.secondary, fontWeight: 600 }}
                >
                  {week.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Días */}
      <Box
        sx={{
          display: 'flex',
          height: DAY_ROW_HEIGHT,
          backgroundColor: pmsColors.ui.surface,
          borderBottom: `1px solid ${pmsColors.ui.divider}`
        }}
      >
        <Box sx={{ position: 'relative', flex: 1, overflow: 'hidden' }}>
          <Box sx={{ position: 'relative', width: timelineWidth, height: '100%' }}>
            {headers.days.map((day, i) => {
              const isToday = day.isToday;
              const isWeekend = day.isWeekend;
              const isHoliday = HOLIDAYS.has(dayjs(day.date).format('YYYY-MM-DD'));
              return (
                <Box
                  key={i}
                  sx={{
                    position: 'absolute',
                    left: day.position,
                    top: 0,
                    width: cellWidth,
                    height: '100%',
                    borderLeft: `1px solid ${pmsColors.ui.border}`,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '2px',
                    backgroundColor: isToday
                      ? '#FFF9E6'
                      : isWeekend
                      ? '#F8FAFF'
                      : pmsColors.ui.surface
                  }}
                >
                  {/* Pastilla circular para "hoy" */}
                  <Box
                    sx={{
                      width: 26,
                      height: 26,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: isToday ? pmsColors.secondary.main : 'transparent',
                      color: isToday ? '#fff' : pmsColors.text.primary,
                      fontWeight: 700,
                      fontSize: '0.85rem',
                      lineHeight: 1,
                      boxShadow: isToday ? '0 2px 6px rgba(99,102,241,.35)' : 'none',
                      position: 'relative'
                    }}
                  >
                    {day.label}
                    {isHoliday && (
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: 2,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          width: 4,
                          height: 4,
                          borderRadius: '50%',
                          backgroundColor: '#DC2626'
                        }}
                      />
                    )}
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: '0.62rem',
                      color: isToday ? pmsColors.text.primary : pmsColors.text.secondary,
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.4px'
                    }}
                  >
                    {day.dayName}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      {/* Franja de tarifa + disponibilidad (solo en vistas multi-día) */}
      {viewMode !== 'hourly' && (
        <Box
          sx={{
            display: 'flex',
            height: RATE_ROW_HEIGHT,
            backgroundColor: '#FAFBFF',
            borderBottom: `1px solid ${pmsColors.ui.border}`
          }}
        >
          <Box sx={{ position: 'relative', flex: 1, overflow: 'hidden' }}>
            <Box sx={{ position: 'relative', width: timelineWidth, height: '100%' }}>
              {headers.days.map((day, i) => {
                const key = dayjs(day.date).format('YYYY-MM-DD');
                const data = computedAvail[key] || { avail: 0, totalRooms: 0 };
                const total = data.totalRooms || 1;
                const ratio = data.avail / total;
                const cls =
                  data.avail === 0 ? 'full' : ratio < 0.2 ? 'low' : 'ok';
                const color =
                  cls === 'full'
                    ? '#DC2626'
                    : cls === 'low'
                    ? '#D97706'
                    : '#16A34A';
                const text =
                  data.avail === 0 ? 'FULL' : `${data.avail} libres`;
                return (
                  <Box
                    key={i}
                    sx={{
                      position: 'absolute',
                      left: day.position,
                      top: 0,
                      width: cellWidth,
                      height: '100%',
                      borderLeft: `1px solid ${pmsColors.ui.border}`,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: day.isWeekend ? '#F4F7FF' : 'transparent'
                    }}
                  >
                    <Typography sx={{ fontSize: '0.66rem', fontWeight: 800, color }}>{text}</Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: pmsColors.ui.surface,
        position: 'relative'
      }}
    >
      {/* Contenedor horizontal scrollable */}
      <Box
        ref={xScrollRef}
        sx={{
          overflowX: 'auto',
          overflowY: 'hidden',
          backgroundColor: pmsColors.ui.header,
          '&::-webkit-scrollbar': { height: 10 },
          '&::-webkit-scrollbar-track': { background: pmsColors.ui.background },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: pmsColors.ui.divider,
            borderRadius: 5,
            border: `2px solid ${pmsColors.ui.background}`
          }
        }}
      >
        <Box sx={{ width: timelineWidth, position: 'relative' }}>
          {viewMode === 'hourly' ? renderHourlyHeader() : renderRangeHeader()}

          {/* GRID */}
          <Box
            sx={{
              height,
              overflowY: 'auto',
              position: 'relative',
              '&::-webkit-scrollbar': { width: 10 },
              '&::-webkit-scrollbar-track': { background: pmsColors.ui.background },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: pmsColors.ui.divider,
                borderRadius: 5,
                border: `2px solid ${pmsColors.ui.background}`
              }
            }}
          >
            {/* Línea vertical "HOY" — solo abarca las filas de habitaciones */}
            {todayLineLeft != null && (
              <Box
                sx={{
                  position: 'absolute',
                  left: todayLineLeft - 1,
                  top: 0,
                  height: Math.min(height, rooms.length * rowHeight),
                  width: 2,
                  backgroundColor: pmsColors.secondary.main,
                  boxShadow: '0 0 0 4px rgba(99,102,241,.10)',
                  borderRadius: 1,
                  pointerEvents: 'none',
                  zIndex: 4
                }}
              />
            )}

            <List
              height={height}
              itemCount={rooms.length}
              itemSize={rowHeight}
              width={timelineWidth}
              overscanCount={5}
              onScroll={handleScroll}
              ref={refList}
            >
              {({ index, style }) => {
                const room = rooms[index];
                const resv = reservationsByRoom.get(String(room.roomNo)) || [];
                return (
                  <Box
                    style={style}
                    sx={{
                      position: 'relative',
                      height: rowHeight,
                      borderBottom: `1px solid ${pmsColors.ui.border}`,
                      backgroundColor: pmsColors.ui.surface
                    }}
                  >
                    {/* Líneas verticales de día (con tinte weekend/today) */}
                    {viewMode !== 'hourly' &&
                      headers.days &&
                      headers.days.map((d, i) => (
                        <Box
                          key={i}
                          sx={{
                            position: 'absolute',
                            left: d.position,
                            top: 0,
                            bottom: 0,
                            width: cellWidth,
                            borderLeft: `1px solid ${pmsColors.ui.border}`,
                            backgroundColor: d.isToday
                              ? 'rgba(255,249,230,0.5)'
                              : d.isWeekend
                              ? 'rgba(248,250,255,0.85)'
                              : 'transparent',
                            pointerEvents: 'none'
                          }}
                        />
                      ))}

                    {/* Líneas verticales de hora (vista horaria) */}
                    {viewMode === 'hourly' &&
                      headers.hours.map((h, i) => (
                        <Box
                          key={i}
                          sx={{
                            position: 'absolute',
                            left: h.hour * cellWidth,
                            top: 0,
                            bottom: 0,
                            width: 1,
                            backgroundColor: h.hour % 6 === 0 ? pmsColors.ui.divider : pmsColors.ui.border,
                            opacity: 0.5,
                            pointerEvents: 'none'
                          }}
                        />
                      ))}

                    {/* Reservas */}
                    {resv.map((r) => (
                      <ReservationBar
                        key={r.id}
                        reservation={r}
                        viewMode={viewMode}
                        rangeStart={rangeStart}
                        cellWidth={cellWidth}
                        totalDays={totalDays}
                        onOpenReservation={onOpenReservation}
                      />
                    ))}
                  </Box>
                );
              }}
            </List>
          </Box>
        </Box>
      </Box>
    </Box>
  );
});

export default TimelinePane;
